import type { ParamType } from "@uirouter/core";

import Sentry from "@similarweb/sentry-logger";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { apiClient } from "app/modules/api-client";
import { ROUTES } from "app/pages/digital-marketing/monitor-and-protect/app/keywords/constants";
import type { MonitorAndProtectParams } from "app/pages/digital-marketing/monitor-and-protect/app/keywords/routes.types";
import type { RedirectHandler } from "app/pages/digital-marketing/monitor-and-protect/shared/redirect-handlers/types";
import queryClient from "single-spa/react-app/query-client";
import { store } from "single-spa/store/store";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import { MONITOR_AND_PROTECT_GET_CAMPAIGN_KEY } from "../../../services/api/campaign";

const appliedFor = [
    ROUTES.COMPETITORS.stateName,
    ROUTES.KEYWORDS.stateName,
    ROUTES.ADS.stateName,
    ROUTES.OVERVIEW.stateName,
] as const;

const fetchCampaign = async (campaignId: string | ParamType) => {
    const campaignIdString = campaignId as string;
    const queryKey = [...MONITOR_AND_PROTECT_GET_CAMPAIGN_KEY, campaignIdString];

    return await queryClient.fetchQuery({
        queryKey,
        queryFn: () => apiClient.MonitorAndProtectMaster.monitorandprotectCampaignsDetail(campaignIdString),
        staleTime: Infinity,
    });
};

export const campaignAccessRedirectHandler: RedirectHandler = async (transition) => {
    const toState = transition.to();
    const toParams = transition.params() as MonitorAndProtectParams;

    if (!appliedFor.includes(toState.name)) {
        return;
    }
    if (!toParams.campaignId) {
        return;
    }

    try {
        await fetchCampaign(toParams.campaignId);
    } catch (error) {
        if (error.status === 404) {
            (store.dispatch as ThunkDispatchCommon)(showErrorToast(translate("rankTracker.reports.campaignNotFound")));

            return {
                state: ROUTES.HOME.stateName,
                params: {
                    ...toParams,
                    campaignId: undefined,
                },
            };
        }
        if (error.status === 403) {
            (store.dispatch as ThunkDispatchCommon)(showErrorToast(translate("monitor_and_protect.nocampaignaccess")));

            return {
                state: ROUTES.HOME.stateName,
                params: {
                    ...toParams,
                    campaignId: undefined,
                },
            };
        }

        Sentry.errorTracker({
            e: error,
            message: "Error in redirect case: campaignAccess",
            level: "error",
            params: {
                extra: {
                    toParams,
                    toState: toState.name,
                },
            },
        });

        return;
    }
};
